import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { GroupsService } from '@shared/services/groups.service';

@Component({
  selector: 'app-email-input',
  templateUrl: './email-input.component.html',
  styleUrls: ['./email-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EmailInputComponent implements OnInit, AfterViewInit {
  @ViewChild('input') input: ElementRef;
  @Input() inputMode = 'email';
  @Input() label = 'Email';
  @Input() required = false;
  @Input() groupId: string | undefined;
  @Input() defaultValue: string = '';
  @Input() invalidEmails: string[] = [];
  @Output() emailsData = new EventEmitter<string[]>();
  @Output() hasValidationError = new EventEmitter<boolean>();

  inputValue = '';
  emails: string[] = [];
  emailCount: number = 0;
  emailInputFormGroup: FormGroup;
  submitted = false;
  customerEmail: string | undefined;
  partnerEmail: string | undefined;
  parentPrimaryEmail: string | undefined;
  accountsEmail: string | undefined;

  constructor(private groupsService: GroupsService) {}

  ngOnInit(): void {
    this.emails = Array.from(new Set((String(this.defaultValue) || '').split(',').filter(Boolean)));
    this.emailCount = this.emails.length;

    this.emailInputFormGroup = new FormGroup({
      input: new FormControl(this.inputValue, [Validators.email]),
    });
    if (this.groupId) {
      this.groupsService.getGroup(this.groupId).subscribe((group) => {
        if (group) {
          this.customerEmail = group.attributes?.contacts?.find((contact) =>
            contact.contact_roles?.some((role) => role === 'primary'),
          )?.email;
          this.partnerEmail = group.attributes?.contacts?.find((contact) =>
            contact.contact_roles?.some((role) => role === 'partner'),
          )?.email;
          this.parentPrimaryEmail = group.attributes?.parent_group_primary_contact?.email;
          this.accountsEmail = group.attributes?.contacts?.find((contact) =>
            contact.contact_roles?.some((role) => role === 'accounts'),
          )?.email;
        }
      });
    }
  }

  ngAfterViewInit() {
    this.input.nativeElement.focus();
  }

  addEmail(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === 'Tab') {
      const trimmedValue = this.emailInputFormGroup.controls['input'].value.trim();
      this.submitted = true;

      if (this.emailInputFormGroup.controls['input'].hasError('email')) return;

      if (trimmedValue) {
        if (this.emails.includes(trimmedValue)) {
          this.emailInputFormGroup.controls['input'].setErrors({ duplicateEmail: true });
        } else if (this.invalidEmails.includes(trimmedValue)) {
          this.emailInputFormGroup.controls['input'].setErrors({ invalidEmail: true });
        } else {
          this.emails.push(trimmedValue);
          this.emailInputFormGroup.controls['input'].setValue('');
          this.emitEmails();
        }
      }
    } else {
      this.submitted = false;
    }
  }

  removeEmail(index: number) {
    this.emails.splice(index, 1);
    this.emitEmails();
  }

  checkValidators() {
    const validators =
      this.required && !this.emails.length ? [Validators.required, Validators.email] : [Validators.email];

    this.emailInputFormGroup.controls['input'].setValidators(validators);
    this.emailInputFormGroup.controls['input'].updateValueAndValidity();
    this.hasValidationError.emit(this.emailInputFormGroup.controls['input'].errors !== null);

    if (this.emailInputFormGroup.controls['input'].errors !== null) this.input.nativeElement.focus();
    if (!this.emailInputFormGroup.controls['input'].hasError('email')) this.addEmail({ key: 'Enter' } as KeyboardEvent);
  }

  emitEmails() {
    this.emailCount = this.emails.length;
    this.checkValidators();
    this.emailsData.emit(this.emails);
  }
}
